import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  GlobalStyles,
  IconButton,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import dogWalking from "../../../assets/dog-walking.gif";
import { useApp } from "../../../app/context/useApp";
import { useAuth } from "../../../app/context/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import FeedbackButtons from "../../../features/generate/components/FeedbackButtons";
import DownloadPdfButton from "../../../features/generate/components/DownloadPdfButton";
import FeedbackModal from "../../../features/generate/components/FeedbackModal";
import SubmittedFeedback, {
  getFeedbackRating,
  normalizeFeedbackEntry,
  normalizeNoteEntry,
} from "../../../features/generate/components/SubmittedFeedback";
import { messageService } from "../../../core/services/message.service";
import { copyMarkdownToClipboard } from "../../../core/utils/clipboard";
import { ensureBlankLineBeforeGFMTables } from "../../../core/utils/markdown";
import { getClientDisplayName } from "../../../core/utils/clientDisplay";
import { getProjectName } from "../../../layout/Header/utils/projectUtils";
import { midDesktopSidebarClearanceSx } from "../../../layout/layoutMetrics";
import { useSidebar } from "../../../layout/SidebarContext";
import { isLockableBriefMessage } from "../../../features/brief/utils/briefLockUtils";

const isTemporaryMessageId = (messageId) =>
  !!messageId && String(messageId).trim().startsWith("msg-");

const getMessageAttachmentNames = (message) => {
  if (Array.isArray(message?.attachmentNames) && message.attachmentNames.length) {
    return message.attachmentNames.map((name) => String(name).trim()).filter(Boolean);
  }
  if (message?.attachmentName) {
    return [String(message.attachmentName).trim()].filter(Boolean);
  }

  const attachment = message?.attachment;
  if (Array.isArray(attachment)) {
    return attachment
      .map((item) =>
        String(typeof item === "string" ? item : item?.name ?? "").trim(),
      )
      .filter(Boolean);
  }
  if (typeof attachment === "string" && attachment.trim()) {
    return [attachment.trim()];
  }
  if (attachment?.name) {
    return [String(attachment.name).trim()].filter(Boolean);
  }
  return [];
};

const STANDARD_LOADING_MESSAGES = [
  "Aligning...",
  "Pressure-testing...",
  "Orchestrating...",
  "Activating...",
  "Optimizing...",
  "Synthesizing...",
  "Mapping...",
  "Having a think…",
  "Mulling...",
  "Connecting...",
  "Building...",
  "Strategizing, not spinning...",
  "Sussing it out...",
  "Activating Performance Branding...",
  "Applying the Performance Branding framework...",
  "Aligning purpose with performance...",
  "Connecting strategy to execution...",
  "Turning strategy into action...",
  "Finding a more elegant solution...",
  "Building momentum...",
  "Optimizing for outcomes...",
  "Preparing your next move...",
  "Mapping the opportunity...",
  "Building a stronger recommendation...",
  "Pressure-testing the approach...",
  "Pressure-testing assumptions...",
  "One last pass...",
  "Orchestrating the work...",
  "Dusting off the bits...",
  "Building shared context...",
  "Connecting the dots... and bots…",
  "Finding the signal...",
  "Looking beyond the brief...",
  "Bringing the pieces together...",
  "Adding in the brand strategic context...",
  "Building alignment with the brief...",
  "Learning from your best work...",
  "Activating brand knowledge...",
  "Connecting deeper with agency beliefs...",
  "Applying brand voice...",
  "Bringing your ecosystem together...",
  "Connecting with best practices...",
  "Building context...",
  "Reviewing available context...",
  "01100001 01101000 01100001! Found something.",
  "Organizing information...",
  "Synthesizing insights...",
  'Checking for the "why"...',
  "Looking around corners...",
  "Finding the strongest path...",
  "Deep thought: the signal is usually quieter than the noise…",
  "Convincing the electrons to cooperate...",
  "Looking for what matters most...",
  "Separating insight from information...",
  "Revising for greater growth impact…",
  "Simplifying some complexities...",
  "Turning context into clarity...",
  "Finding the thread...",
  "Preparing your results...",
];

const EASTER_EGG_LOADING_MESSAGES = [
  "Looking for the one thing that changes everything...",
  "One more pressure test...",
  "Challenging my answer...",
  "Looking for the white space...",
  "Connecting a few unexpected dots...",
  "Looking for the signal behind the data...",
  "Finding the story beneath the signal...",
  "Building something worth sharing...",
  "Making complexity more approachable...",
  "Finding the simplest path forward...",
  "Every great idea starts somewhere...",
  "Sharpening the thinking...",
  "If I had eyebrows, this is where I'd raise one…",
  "Avoiding countless rabbit holes...",
  "Keeping the good ideas. Leaving the rest.",
  "Looking for what everyone else missed...",
  "Giving this one a last look...",
  "Looking for the next breakthrough...",
  "Making the next step a little clearer...",
  "Good work is worth a few extra seconds…",
  "Taking the scenic route through the data...",
  "The details matter…",
  "Deep thought: every masterpiece was once an uncomfortable first draft…",
  "Looking for what's missing...",
  "Clarity beats complexity…",
  "Trusting the data, but also verifying…",
  "Great work compounds…",
  "Deep thought: sunrises are like a loading screen for the day",
];

const pickLoadingMessage = (lastMessage) => {
  const useEasterEgg = Math.random() < 0.1;
  const pool = useEasterEgg
    ? EASTER_EGG_LOADING_MESSAGES
    : STANDARD_LOADING_MESSAGES;
  const candidates = pool.filter((message) => message !== lastMessage);
  const list = candidates.length > 0 ? candidates : pool;
  return list[Math.floor(Math.random() * list.length)];
};

const randomLoadingIntervalMs = () =>
  4000 + Math.floor(Math.random() * 1001);

const assistantMarkdownComponents = {
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: ({ node, children, ...props }) => (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        my: 1.25,
      }}
    >
      <Box
        component="table"
        {...props}
        sx={{
          borderCollapse: "collapse",
          borderSpacing: 0,
          width: "100%",
          minWidth: 480,
          tableLayout: "auto",
          border: "1px solid rgba(255,255,255,0.14)",
        }}
      >
        {children}
      </Box>
    </Box>
  ),
};

const assistantMarkdownContainerSx = {
  "& p": {
    marginTop: 0,
    marginBottom: "1em",
    "&:last-child": { marginBottom: 0 },
  },
  "& ul, & ol": {
    marginTop: 0,
    marginBottom: "1em",
    paddingLeft: "1.35em",
  },
  "& a": {
    color: "#7EB6FF",
    textDecoration: "underline dotted",
    textUnderlineOffset: "3px",
    textDecorationThickness: "1px",
    textDecorationColor: "rgba(126, 182, 255, 0.55)",
    transition: "color 150ms ease, text-decoration-color 150ms ease",
    "&:visited": {
      color: "#A8B4E8",
      textDecorationColor: "rgba(168, 180, 232, 0.5)",
    },
    "&:hover": {
      color: "#A8D0FF",
      textDecoration: "underline solid",
      textDecorationColor: "rgba(168, 208, 255, 0.85)",
    },
  },
  "& thead": { display: "table-header-group" },
  "& tbody": { display: "table-row-group" },
  "& tr": { display: "table-row" },
  "& th, & td": {
    display: "table-cell",
    textAlign: "left",
    verticalAlign: "top",
    padding: "10px 14px",
    lineHeight: 1.5,
    border: "1px solid rgba(255,255,255,0.14)",
  },
  "& th": {
    fontWeight: 600,
  },
  "& code": {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "0.9em",
  },
};

const SERVER_ERROR_MESSAGE = "Oops! Something went wrong. Please try again later.";

const isAssistantErrorMessage = (text) => {
  const value = String(text || "").trim();
  return (
    value === SERVER_ERROR_MESSAGE ||
    /^Error:/i.test(value) ||
    /networkerror|network error|failed to fetch/i.test(value)
  );
};

const getAssistantErrorMessage = (text) =>
  /networkerror|network error|failed to fetch/i.test(String(text || ""))
    ? SERVER_ERROR_MESSAGE
    : String(text || SERVER_ERROR_MESSAGE).replace(/^Error:\s*/i, "").trim();

const hasOwn = (source, key) =>
  Object.prototype.hasOwnProperty.call(source || {}, key);

const getFeedbackResponseSource = (response) => {
  const candidates = [
    response,
    response?.data,
    response?.message,
    response?.result,
    Array.isArray(response?.data) ? response.data[0] : null,
  ];

  return candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      (hasOwn(candidate, "rating") ||
        hasOwn(candidate, "Rating") ||
        hasOwn(candidate, "comment") ||
        hasOwn(candidate, "Comment")),
  );
};

const normalizeFeedbackResponse = (response, fallbackFeedback) => {
  const source = getFeedbackResponseSource(response);
  const hasApiComment = hasOwn(source, "comment") || hasOwn(source, "Comment");
  const apiRating = source?.rating ?? source?.Rating;
  const apiComment = source?.comment ?? source?.Comment;

  return normalizeFeedbackEntry({
    rating: apiRating ?? fallbackFeedback.rating,
    comment: hasApiComment ? apiComment : fallbackFeedback.comment,
  });
};

const getNoteResponseSource = (response) => {
  const candidates = [
    response,
    response?.data,
    response?.message,
    response?.result,
    Array.isArray(response?.data) ? response.data[0] : null,
  ];

  return candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      (hasOwn(candidate, "note") ||
        hasOwn(candidate, "Note") ||
        hasOwn(candidate, "notes") ||
        hasOwn(candidate, "Notes") ||
        hasOwn(candidate, "noteText") ||
        hasOwn(candidate, "NoteText") ||
        hasOwn(candidate, "messageNote") ||
        hasOwn(candidate, "MessageNote") ||
        hasOwn(candidate, "messageNotes") ||
        hasOwn(candidate, "MessageNotes") ||
        hasOwn(candidate, "message_note")),
  );
};

const normalizeNoteResponse = (response, fallbackNote) => {
  const source = getNoteResponseSource(response);

  return normalizeNoteEntry(source ?? fallbackNote);
};

export default function ChatHistory({ showChatModeToggle = false }) {
  const {
    messages,
    setMessages,
    selectedClient,
    selectedProject,
    isGenerating,
    currentChatSession,
    chatType,
    setChatType,
  } = useApp();
  const { userInfo } = useAuth();
  const { isSidebarOpen } = useSidebar();
  const midSidebarClearance = midDesktopSidebarClearanceSx(isSidebarOpen);
  const scrollContainerRef = useRef(null);
  const [stickToBottom, setStickToBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackByMessageId, setFeedbackByMessageId] = useState({});
  const [submittingNote, setSubmittingNote] = useState(false);
  const [noteByMessageId, setNoteByMessageId] = useState({});
  const [lockingMessageId, setLockingMessageId] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(
    () => STANDARD_LOADING_MESSAGES[0],
  );
  const loadingMessageRef = useRef(loadingMessage);
  const loadingTimeoutRef = useRef(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const selectedClientId =
    selectedClient?.clientId ?? selectedClient?.Id ?? selectedClient?.id ?? null;
  const selectedProjectId =
    selectedProject?.projectId ?? selectedProject?.Id ?? selectedProject?.id ?? null;
  const selectedClientName = useMemo(
    () => getClientDisplayName(selectedClient),
    [selectedClient],
  );
  const selectedProjectName = useMemo(
    () => String(getProjectName(selectedProject) ?? "").trim(),
    [selectedProject],
  );
  const scopeLabel = selectedProjectName || selectedClientName;
  const currentUserId = String(userInfo?.id ?? "");
  const currentUserName = String(userInfo?.username ?? userInfo?.name ?? "");
  const chatModeOptions = [
    { value: "global", label: "Team Chat" },
    { value: "private", label: "Private Chat" },
  ];

  useEffect(() => {
    const clearLoadingTimeout = () => {
      if (loadingTimeoutRef.current != null) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };

    if (!isGenerating) {
      clearLoadingTimeout();
      return clearLoadingTimeout;
    }

    const initialMessage = pickLoadingMessage(null);
    loadingMessageRef.current = initialMessage;
    setLoadingMessage(initialMessage);

    const scheduleNext = () => {
      loadingTimeoutRef.current = setTimeout(() => {
        const nextMessage = pickLoadingMessage(loadingMessageRef.current);
        loadingMessageRef.current = nextMessage;
        setLoadingMessage(nextMessage);
        scheduleNext();
      }, randomLoadingIntervalMs());
    };

    scheduleNext();

    return clearLoadingTimeout;
  }, [isGenerating]);


  const isAtBottom = useCallback((element) => {
    if (!element) return true;
    const threshold = 50;
    return (
      element.scrollHeight - element.scrollTop <= element.clientHeight + threshold
    );
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const atBottom = isAtBottom(container);
    setStickToBottom(atBottom);
    setShowScrollButton(!atBottom);
  }, [isAtBottom]);

  const jumpToBottom = useCallback((behavior = "smooth", bottomOffset = 0) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const nextTop = Math.max(
      0,
      container.scrollHeight - container.clientHeight - bottomOffset,
    );
    container.scrollTo({
      top: nextTop,
      behavior,
    });
    setStickToBottom(true);
    setShowScrollButton(false);
  }, []);

  useEffect(() => {
    if (!stickToBottom) return;
    // Keep a little breathing room while streaming so the view doesn't feel
    // hard-locked to the absolute end.
    const streamBottomOffset = isGenerating ? 84 : 0;
    jumpToBottom("smooth", streamBottomOffset);
  }, [messages, stickToBottom, isGenerating, jumpToBottom]);

  useEffect(() => {
    const feedbackFromApi = (messages || []).reduce((acc, msg) => {
      const messageId = msg.messagesId ?? msg.id;
      const apiRating = msg.rating;
      const apiComment = msg.comment ?? msg.Comment ?? "";

      if (!messageId || !apiRating) return acc;
      acc[messageId] = {
        rating: apiRating,
        comment: apiComment,
      };
      return acc;
    }, {});

    setFeedbackByMessageId((prev) => ({
      ...prev,
      ...feedbackFromApi,
    }));

    const notesFromApi = (messages || []).reduce((acc, msg) => {
      const messageId = msg.messagesId ?? msg.id;
      const savedNote = normalizeNoteEntry(msg);

      if (!messageId || !savedNote) return acc;
      acc[messageId] = savedNote;
      return acc;
    }, {});

    setNoteByMessageId((prev) => ({
      ...prev,
      ...notesFromApi,
    }));
  }, [messages]);

  const chatTitle = useMemo(() => {
    if (!Array.isArray(messages)) return "New Chat";
    const live = messages.length ? messages : null;
    return currentChatSession?.title || currentChatSession?.silosName || 'New Chat';
  }, [messages, currentChatSession]);

  const activeConversationId =
    currentChatSession?.conversationsId || currentChatSession?.conversationId || null;
  const getConversationIdFromMessage = (messageId) => {
    const target = (messages || []).find(
      (msg) => String(msg.messagesId ?? msg.id) === String(messageId),
    );
    return (
      target?.conversationId ??
      target?.conversationsId ??
      target?.ConversationId ??
      target?.ConversationsId ??
      null
    );
  };

  const buildFeedbackPayload = (
    messageId,
    selectedRating,
    feedbackComment = "",
    messageConversationId = null,
  ) => {
    const payload = {
      rating: selectedRating,
      Comment: feedbackComment,
    };

    if (messageId && !isTemporaryMessageId(messageId)) {
      payload.messagesId = messageId;
      return payload;
    }

    const resolvedConversationId =
      messageConversationId ?? getConversationIdFromMessage(messageId) ?? activeConversationId;

    if (resolvedConversationId) {
      payload.conversationId = resolvedConversationId;
    }

    return payload;
  };

  const buildNotePayload = (
    messageId,
    noteText = "",
    messageConversationId = null,
  ) => {
    const payload = {
      note: noteText,
    };

    if (messageId && !isTemporaryMessageId(messageId)) {
      payload.messagesId = messageId;
      return payload;
    }

    const resolvedConversationId =
      messageConversationId ?? getConversationIdFromMessage(messageId) ?? activeConversationId;

    if (resolvedConversationId) {
      payload.conversationId = resolvedConversationId;
    }

    return payload;
  };

  const canManageNote = (savedNote) => {
    if (!savedNote) return true;
    const noteUserId = String(savedNote.userId ?? "");
    const noteUserName = String(savedNote.userName ?? "");

    if (noteUserId && currentUserId) {
      return noteUserId === currentUserId;
    }
    if (noteUserName && currentUserName) {
      return noteUserName.toLowerCase() === currentUserName.toLowerCase();
    }

    return false;
  };

  const handleFeedbackClick = async (messageId, selectedRating, messageConversationId = null) => {
    if (!messageId && !messageConversationId && !activeConversationId) return;

    const savedRating = getFeedbackRating(feedbackByMessageId[messageId]);
    const isDeselectingSaved =
      !selectedRating && savedRating && (!open || selectedMessageId !== messageId);
    if (isDeselectingSaved) {
      if (submittingFeedback) return;
      try {
        setSubmittingFeedback(true);
        const payload = buildFeedbackPayload(messageId, null, "", messageConversationId);
        await messageService.feedbackMessage(payload);
        setFeedbackByMessageId((prev) => {
          const next = { ...prev };
          delete next[messageId];
          return next;
        });
      } catch (error) {
        
      } finally {
        setSubmittingFeedback(false);
      }
      return;
    }
    
    if (open && selectedMessageId === messageId && rating === selectedRating) {
      handleCloseModal();
      return;
    }

    if (!selectedRating) {
      handleCloseModal();
      return;
    }

    setSelectedMessageId(messageId);
    setRating(selectedRating);
    setComment(
      normalizeFeedbackEntry(feedbackByMessageId[messageId])?.comment ?? "",
    );
    setOpen(true);
  };

  const handleEditFeedback = (messageId) => {
    if (submittingFeedback) return;

    const savedFeedback = normalizeFeedbackEntry(feedbackByMessageId[messageId]);
    if (!savedFeedback) return;

    setSelectedMessageId(messageId);
    setRating(savedFeedback.rating);
    setComment(savedFeedback.comment);
    setOpen(true);
  };

  const handleCloseModal = () => {
    if (submittingFeedback) return;
    setOpen(false);
    setComment("");
    setRating(null);
    setSelectedMessageId(null);
  };

  const handleSubmitFeedback = async () => {
    const payload = buildFeedbackPayload(selectedMessageId, rating, comment);

    if (
      !rating ||
      (!payload.messagesId && !payload.conversationId) ||
      submittingFeedback
    ) {
      return;
    }

    try {
      setSubmittingFeedback(true);
      const response = await messageService.feedbackMessage(payload);
      const savedFeedback = normalizeFeedbackResponse(response, {
        rating,
        comment: comment.trim(),
      });

      setFeedbackByMessageId((prev) => ({
        ...prev,
        [selectedMessageId]: savedFeedback,
      }));
      handleCloseModal();
    } catch (error) {
      // Keep modal open so user can retry.
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleSaveNote = async (
    messageId,
    noteText,
    messageConversationId = null,
  ) => {
    const payload = buildNotePayload(
      messageId,
      String(noteText ?? "").trim(),
      messageConversationId,
    );

    if (!payload.note || (!payload.messagesId && !payload.conversationId) || submittingNote) {
      return false;
    }

    try {
      setSubmittingNote(true);
      const response = await messageService.saveMessageNote(payload);
      const savedNote = normalizeNoteResponse(response, {
        note: payload.note,
        userName: currentUserName,
        userId: currentUserId,
      });

      setNoteByMessageId((prev) => ({
        ...prev,
        [messageId]: savedNote,
      }));
      return true;
    } catch (error) {
      return false;
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleRemoveNote = async (messageId, messageConversationId = null) => {
    const payload = buildNotePayload(messageId, "", messageConversationId);

    if ((!payload.messagesId && !payload.conversationId) || submittingNote) {
      return false;
    }

    try {
      setSubmittingNote(true);
      await messageService.saveMessageNote(payload);
      setNoteByMessageId((prev) => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
      return true;
    } catch (error) {
      return false;
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleCopyMessage = async (msgId, text) => {
    const plain = String(text ?? "").trim();
    if (!plain) return;
    try {
      const copied = await copyMarkdownToClipboard(plain);
      if (!copied) return;
      setCopiedMessageId(msgId);
      window.setTimeout(() => setCopiedMessageId(null), 2000);
    } catch {
      // Clipboard may be blocked (non-HTTPS, permissions).
    }
  };

  const buildLockPayload = (messageId, isLock) => {
    const payload = {
      messagesId: messageId,
      clientId: selectedClientId,
      isLock: !!isLock,
    };

    if (selectedProjectId != null) {
      payload.projectId = selectedProjectId;
    }

    return payload;
  };

  const handleLockMessage = async (messageId, currentlyLocked) => {
    if (
      !messageId ||
      isTemporaryMessageId(messageId) ||
      selectedClientId == null ||
      lockingMessageId
    ) {
      return;
    }

    const nextIsLock = !currentlyLocked;

    // Only allow locking replies to /generate; unlock always allowed.
    if (nextIsLock) {
      const messageIndex = (messages || []).findIndex(
        (msg) => String(msg.messagesId ?? msg.id) === String(messageId),
      );
      const targetMessage = messageIndex >= 0 ? messages[messageIndex] : null;
      if (
        !isLockableBriefMessage({
          message: targetMessage,
          messages,
          messageIndex,
          isAlreadyLocked: false,
        })
      ) {
        return;
      }
    }

    try {
      setLockingMessageId(messageId);
      await messageService.isLockMessage(
        buildLockPayload(messageId, nextIsLock),
      );

      setMessages((prev) =>
        (prev || []).map((msg) => {
          const id = msg.messagesId ?? msg.id;
          if (String(id) === String(messageId)) {
            return { ...msg, isLock: nextIsLock };
          }
          // Backend unlocks any previously locked message when locking a new one.
          if (nextIsLock && msg.isLock) {
            return { ...msg, isLock: false };
          }
          return msg;
        }),
      );
    } catch {
      // Keep current lock state on failure.
    } finally {
      setLockingMessageId(null);
    }
  };

  const chatModeToggle = (
    <Box
      role="group"
      aria-label="Chat visibility"
      sx={{
        position: "relative",
        width: 148,
        height: 30,
        p: "3px",
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "999px",
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.045))",
        border: "1px solid rgba(255,255,255,0.16)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -10px 22px rgba(0,0,0,0.08), 0 12px 28px rgba(0,0,0,0.2)",
        backdropFilter: "blur(26px) saturate(122%)",
        WebkitBackdropFilter: "blur(26px) saturate(122%)",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 3,
          bottom: 3,
          left: chatType === "global" ? 3 : "calc(50% + 0px)",
          width: "calc(50% - 3px)",
          borderRadius: "999px",
          background:
            "radial-gradient(circle at 25% 20%, rgba(255,255,255,0.42), transparent 34%), linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08))",
          border: "1px solid rgba(255,255,255,0.24)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.32), inset 0 -8px 18px rgba(0,0,0,0.08), 0 8px 20px rgba(0,0,0,0.22)",
          transition: "left 260ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />
      {chatModeOptions.map((option) => {
        const isActive = chatType === option.value;
        return (
          <Button
            key={option.value}
            size="small"
            disableElevation
            disableRipple
            aria-pressed={isActive}
            onClick={() => setChatType(option.value)}
            sx={{
              position: "relative",
              zIndex: 1,
              flex: 1,
              minWidth: 0,
              height: "100%",
              px: 0.7,
              py: 0,
              borderRadius: "999px",
              textTransform: "none",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.015em",
              color: isActive ? "rgba(255,255,255,0.96)" : "rgba(234,231,226,0.58)",
              background: "transparent",
              border: "none",
              boxShadow: "none",
              transition: "color 180ms ease, opacity 180ms ease",
              "&:hover": {
                background: "transparent",
                color: "#fff",
              },
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        color: "#EAE7E2",
      }}
    >
      <GlobalStyles
        styles={`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500&family=Poppins:wght@500;600&display=swap');

        @keyframes blink {
          0% { opacity: 1; }
          50% { opacity: 0; }
          100% { opacity: 1; }
        }
      `}
      />

     
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          px: { xs: 4, md: 20, lg: 42 },
          py: 2,
          background: "transparent",
          [`@media (max-width: 767px)`]: {
            px: 1.5,
            py: 1.25,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          },
          ...midSidebarClearance,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            minWidth: 0,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            [`@media (max-width: 767px)`]: {
              gap: 1,
              flexWrap: "wrap",
            },
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: "white",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: "'Inter', sans-serif !important",
              [`@media (max-width: 767px)`]: {
                fontSize: 14,
                flex: "1 1 auto",
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
          >
            {scopeLabel ? `${scopeLabel} : ${chatTitle}` : chatTitle}
          </Typography>
          {showChatModeToggle && (
            <Box
              sx={{
                flexShrink: 0,
                ml: "auto",
                [`@media (max-width: 767px)`]: { ml: 0 },
              }}
            >
              {chatModeToggle}
            </Box>
          )}
        </Box>
        <Divider sx={{ mt: 1.5, borderColor: "rgba(255,255,255,0.15)" }} />
      </Box>

    
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          py: 4,
          px: { xs: 4, md: 20, lg: 42 },
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.25)",
            borderRadius: "10px",
          },
          [`@media (max-width: 767px)`]: {
            overflowX: "hidden",
            py: 2,
            px: 1.5,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          },
          ...midSidebarClearance,
        }}
      >
        <Box
          sx={{
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent:
              messages.length === 0 && !isGenerating ? "center" : "flex-end",
            gap: 3,
          }}
        >
          {messages.length === 0 && !isGenerating && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(234, 231, 226, 0.6)",
                  fontSize: 16,
                  fontFamily: "'Inter', sans-serif !important",
                }}
              >
                Define your objectives...
              </Typography>
            </Box>
          )}

          {messages.map((m, index) => {
          const isUser = m.role === "user";
          const displayText = m.displayContent ?? m.content;
          const attachmentNames = getMessageAttachmentNames(m);
          const isLastAI = !isUser && index === messages.length - 1;
          const isAssistantError = !isUser && isAssistantErrorMessage(displayText);
          const safeAssistantErrorText = getAssistantErrorMessage(displayText);
          const isTyping =
            isLastAI && isGenerating && String(displayText || "").length > 0;
          const hasTypingEffectInProgress =
            !isUser &&
            typeof m.displayContent === "string" &&
            typeof m.content === "string" &&
            m.displayContent !== m.content;
          const isResponseCompleted =
            !isUser &&
            !!displayText?.trim() &&
            !(isLastAI && isGenerating) &&
            !hasTypingEffectInProgress;
          const messageId = m.messagesId ?? m.id;
          const messageConversationId =
            m.conversationId ?? m.conversationsId ?? m.ConversationId ?? m.ConversationsId ?? null;
          const savedUserFeedback = normalizeFeedbackEntry(
            feedbackByMessageId[messageId],
          );
          const savedMessageNote = normalizeNoteEntry(noteByMessageId[messageId]);
          const selectedFeedbackRating =
            savedUserFeedback?.rating ||
            (open && selectedMessageId === messageId ? rating : null);
          const isMessageLocked = !!m.isLock;
          const canLockAsBrief = isLockableBriefMessage({
            message: m,
            messages,
            messageIndex: index,
            isAlreadyLocked: isMessageLocked,
          });
          const canToggleLock =
            !!messageId &&
            !isTemporaryMessageId(messageId) &&
            selectedClientId != null &&
            canLockAsBrief;

          return (
            <Box
              key={m.id}
              sx={{
                display: "flex",
                justifyContent: isUser ? "flex-end" : "flex-start",
              }}
            >
              <Box
                sx={{
                  maxWidth: isUser ? "80%" : "100%",
                  [`@media (max-width: 767px)`]: {
                    maxWidth: isUser ? "92%" : "100%",
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  },
                }}
              >
                <Box
                  sx={{
                    fontSize: isUser ? 15 : 14.2,
                    lineHeight: isUser ? 1.5 : 1.8,
                    fontFamily: isUser
                      ? "'Poppins', sans-serif"
                      : "'Inter', sans-serif",
                    fontWeight: isUser ? 500 : 400,
                    letterSpacing: isUser ? "0.2px" : "0px",
                    color: isUser ? "white" : "rgba(234,231,226,0.9)",
                    background: isUser
                // ? "rgba(88, 101, 242, 0.35)"
                                    ? "rgba(255,255,255,0.08)"

                      : "transparent",
                    p: isUser ? "10px 16px" : isAssistantError ? 2 : 0,
                    borderRadius: isUser
                      ? "16px 16px 4px 16px"
                      : isAssistantError
                        ? 4
                        : 0,
                    [`@media (max-width: 767px)`]: {
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    },
                    ...(isAssistantError
                      ? {
                          maxWidth: 520,
                          position: "relative",
                          overflow: "hidden",
                          background:
                            "radial-gradient(circle at 14% 18%, rgba(219,165,41,0.28), transparent 28%), linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06))",
                          border: "1px solid rgba(219,165,41,0.2)",
                          backdropFilter: "blur(22px) saturate(120%)",
                          WebkitBackdropFilter: "blur(22px) saturate(120%)",
                          boxShadow:
                            "inset 0 1px 0 rgba(255,255,255,0.18), 0 18px 42px rgba(0,0,0,0.2)",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 12,
                            bottom: 12,
                            width: 3,
                            borderRadius: "999px",
                            background: "linear-gradient(180deg, #f3c94a, rgba(219,165,41,0.18))",
                          },
                        }
                      : {}),
                  }}
                >
              
                  {isUser ? (
                    <>
                      {attachmentNames.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            mb: displayText ? 0.75 : 0,
                          }}
                        >
                          {attachmentNames.map((name) => (
                        <Box
                              key={name}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            opacity: 0.9,
                          }}
                        >
                          <AttachFileIcon sx={{ fontSize: 16 }} />
                          <Box
                            component="span"
                            sx={{
                              fontSize: 13,
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {name}
                          </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                      {displayText}
                    </>
                  ) : isAssistantError ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "rgba(219, 165, 41, 0.12)",
                          border: "1px solid rgba(219, 165, 41, 0.32)",
                          boxShadow: "0 0 22px rgba(219,165,41,0.14)",
                          flexShrink: 0,
                        }}
                      >
                        <ErrorOutlineRoundedIcon sx={{ color: "#f3c94a", fontSize: 22 }} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            color: "rgba(243,201,74,0.9)",
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            mb: 0.25,
                          }}
                        >
                          Signal interrupted
                        </Typography>
                        <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: 650, lineHeight: 1.45 }}>
                          {safeAssistantErrorText || SERVER_ERROR_MESSAGE}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={assistantMarkdownContainerSx}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={assistantMarkdownComponents}
                      >
                        {ensureBlankLineBeforeGFMTables(
                          String(displayText || ""),
                        )}
                      </ReactMarkdown>
                    </Box>
                  )}
                  {isTyping && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: "7px",
                        height: "14px",
                        bgcolor: "white",
                        ml: 0.8,
                        verticalAlign: "middle",
                        animation: "blink 1s step-end infinite",
                      }}
                    />
                  )}
                </Box>
                {isResponseCompleted && (
                  <SubmittedFeedback
                    feedback={savedUserFeedback}
                    note={savedMessageNote}
                    canManageNote={canManageNote(savedMessageNote)}
                    disabled={submittingFeedback || submittingNote}
                    onEdit={() => handleEditFeedback(messageId)}
                    onRemove={() =>
                      handleFeedbackClick(
                        messageId,
                        null,
                        messageConversationId,
                      )
                    }
                    onSaveNote={(noteText) =>
                      handleSaveNote(messageId, noteText, messageConversationId)
                    }
                    onRemoveNote={() =>
                      handleRemoveNote(messageId, messageConversationId)
                    }
                    trailingActions={
                      selectedClientId && canLockAsBrief ? (
                        <Tooltip title={isMessageLocked ? "Unlock" : "Lock"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleLockMessage(messageId, isMessageLocked)
                            }
                            disabled={
                              !canToggleLock || lockingMessageId === messageId
                            }
                            aria-label={
                              isMessageLocked
                                ? "Unlock message"
                                : "Lock message"
                            }
                            sx={{
                              color: isMessageLocked
                                  ? "rgba(246,192,78,0.98)"
                                : "rgba(255,255,255,0.7)",
                            }}
                          >
                            {isMessageLocked ? (
                              <LockOutlinedIcon fontSize="small" />
                            ) : (
                              <LockOpenOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      ) : null
                    }
                  >
                    {({ isFeedbackOpen, toggleFeedback }) => (
                      <>
                        <Tooltip
                          title={copiedMessageId === messageId ? "Copied!" : "Copy"}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopyMessage(messageId, displayText)
                              }
                              aria-label="Copy message"
                              sx={{
                                color:
                                  copiedMessageId === messageId
                                    ? "rgba(124, 252, 152, 0.95)"
                                    : "rgba(255,255,255,0.7)",
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <DownloadPdfButton markdown={displayText} />
                        <FeedbackButtons
                          selectedRating={selectedFeedbackRating}
                          disabled={
                            (!messageId &&
                              !messageConversationId &&
                              !activeConversationId) ||
                            (isTemporaryMessageId(messageId) &&
                              !messageConversationId &&
                              !activeConversationId) ||
                            submittingFeedback
                          }
                          onSelect={(selectedRating) =>
                            handleFeedbackClick(
                              messageId,
                              selectedRating,
                              messageConversationId,
                            )
                          }
                          onSelectedClick={
                            savedUserFeedback ? toggleFeedback : undefined
                          }
                          selectedTooltip={
                            savedUserFeedback && !isFeedbackOpen
                              ? "View feedback"
                              : ""
                          }
                        />
                      </>
                    )}
                  </SubmittedFeedback>
                )}
              </Box>
            </Box>
          );
          })}

          {isGenerating && !messages[messages.length - 1]?.displayContent && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
              <img src={dogWalking} alt="dog walking" width={65} height={65} /> 
              <Typography
                sx={{
                  fontSize: 13.2,
                  color: "rgba(255,255,255,0.4)",
                  fontFamily: "'Inter', sans-serif !important",
                }}
              >
                {loadingMessage}
              </Typography>
            </Box>
          )}
          {isGenerating && (
            <Box
              sx={{
                height: { xs: 152, md: 196 },
                flexShrink: 0,
              }}
            />
          )}
        </Box>

      </Box>
      {showScrollButton && (
        <IconButton
          aria-label="Jump to latest message"
          onClick={() => jumpToBottom("smooth")}
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 16,
            zIndex: 8,
            width: 38,
            height: 38,
            bgcolor: "transparent",
            color: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(2px)",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.34)",
              borderColor: "rgba(255,255,255,0.34)",
            },
          }}
        >
          <KeyboardArrowDownRoundedIcon />
        </IconButton>
      )}

      <FeedbackModal
        open={open}
        rating={rating}
        comment={comment}
        loading={submittingFeedback}
        onClose={handleCloseModal}
        onCommentChange={setComment}
        onSubmit={handleSubmitFeedback}
      />
    </Box>
  );
}
